import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { slk } from 'survey-core';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';

import { Checkbox } from '@mui/material';

import { loadQuestionnaireTypes } from '../../store/directusService';
import {
    createQuestionnaire,
    loadQuestionnaires,
    updateQuestionnaire
} from '../../store/participantService';
import { POLARIS_ROYAL_BLUE } from '../../themes/theme';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import SingleSelect from '../formElements/SingleSelect';
import ScreenError from '../ui/messageBanners/ScreenError';

import { themeJson } from './theme';
import { validate } from './validateQuestionnaire';

import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';
import form from '../../commonStyles/formStyles.module.css';
import local from './questionnaireStyles/questionnaireStyle.module.css';
import classes from './questionnaireStyles/questionnaireToolbar.module.css';

const VITE_APP_SURVEYJS_BUILDER_LICENCE = import.meta.env.VITE_APP_SURVEYJS_BUILDER_LICENCE;

const acceptedRoles = [SUPERUSER];

const QuestionnaireBuilder = () => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    slk(VITE_APP_SURVEYJS_BUILDER_LICENCE);

    const questionTypes = [
        'text',
        'comment',
        'checkbox',
        'radiogroup',
        'dropdown',
        'boolean',
        'rating',
        'matrix',
        'matrixdropdown',
        'matrixdynamic',
        'multipletext',
        'panel',
        'paneldynamic',
        'expression',
        'ranking',
        'buttongroup',
        'flowpanel'
    ];

    const [creator, setCreator] = useState(
        new SurveyCreator({
            showDesignerTab: true,
            showQuestionNumbers: true,
            showLogicTab: true,
            showThemeTab: false,
            showJSONEditorTab: true,
            isAutoSave: false,
            questionTypes: questionTypes
        })
    );
    const [questionnaireChanged, setQuestionnaireChanged] = useState(false);
    const [inactive, setInactive] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentQuestionnaire = useSelector(
        (state) => state.entities.participantService.currentQuestionnaire
    );
    const questionnaireAnswers = useSelector(
        (state) => state.entities.participantService.questionnaireAnswers
    );
    const questionnaireTypes = useSelector(
        (state) => state.entities.directusService.questionnaireTypes
    );

    // USE EFFECTS
    useEffect(() => {
        if (currentQuestionnaire && Object.keys(currentQuestionnaire).length > 0)
            setInactive(currentQuestionnaire.inactive);

        dispatch(loadQuestionnaireTypes());
    }, []);

    useEffect(() => {
        if (questionnaireChanged) {
            dispatch(loadQuestionnaires());
            navigate('/manage_questionnaire');
        }
    }, [questionnaireChanged]);

    useEffect(() => {
        const hasResults =
            currentQuestionnaire &&
            Object.keys(currentQuestionnaire).length &&
            questionnaireAnswers &&
            questionnaireAnswers.length > 0;

        const surveyOptions = {
            showDesignerTab: !hasResults,
            showLogicTab: !hasResults,
            showThemeTab: false,
            showJSONEditorTab: true,
            isAutoSave: false,
            questionTypes: questionTypes
        };

        setCreator(new SurveyCreator(surveyOptions));
    }, [questionnaireAnswers]);

    useEffect(() => {
        creator.theme = themeJson;

        if (currentQuestionnaire && Object.keys(currentQuestionnaire).length > 0) {
            creator.text = JSON.stringify(currentQuestionnaire.data);
        }

        if (questionnaireAnswers.length === 0) {
            creator.saveSurveyFunc = () => {
                onSubmitQuestionnaire();
            };
        }
    }, [creator, currentQuestionnaire, questionnaireAnswers]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onQuestionnaireTypeChange = (chosenId) => {
        if (
            !currentQuestionnaire ||
            Object.keys(currentQuestionnaire).length < 1 ||
            (!currentQuestionnaire.typeId && chosenId === '') ||
            currentQuestionnaire.typeId === chosenId
        )
            return;
        const payload = {
            ...currentQuestionnaire,
            typeId: chosenId
        };
        dispatch(updateQuestionnaire(payload));
    };

    const onSubmitQuestionnaire = () => {
        const newErrors = validate(creator.survey);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (currentQuestionnaire && Object.keys(currentQuestionnaire).length > 0) {
            const payload = {
                id: currentQuestionnaire.id,
                title: creator.survey.title,
                code: currentQuestionnaire.code,
                date_created: currentQuestionnaire.date_created,
                data: JSON.parse(creator.text),
                inactive: inactive,
                typeId: currentQuestionnaire.typeId
            };
            dispatch(updateQuestionnaire(payload));
        } else {
            const payload = {
                title: creator.survey.title,
                data: JSON.parse(creator.text),
                inactive: false
            };
            dispatch(createQuestionnaire(payload));
        }
        setQuestionnaireChanged(true);
    };

    const onCheck = (e) => {
        const { checked } = e.target;

        const newInactiveState = !checked;
        setInactive(newInactiveState);

        const payload = {
            ...currentQuestionnaire,
            inactive: newInactiveState
        };

        dispatch(updateQuestionnaire(payload));
    };

    // RENDER
    return (
        <div className={local.wrapper}>
            {Object.values(errors).some((el) => el.error) && (
                <div className={local.questionnaireBuilderErrorWrapper}>
                    {Object.entries(errors).map((el) =>
                        el[1].error ? (
                            <div key={el[0]}>
                                <ScreenError
                                    error={el[1]}
                                    name={el[0]}
                                    clearError={clearError}
                                    customClass="relativePos"
                                />
                            </div>
                        ) : null
                    )}
                </div>
            )}

            <div className={classes.questionnaireBuilder}>
                <SurveyCreatorComponent creator={creator} />
            </div>
            <form className={form.formWrapper} data-testid="form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <label htmlFor="activeCheckbox">Active</label>
                        <Checkbox
                            data-testid="checkbox_start"
                            key={currentQuestionnaire?.id || 'id'}
                            type="checkbox"
                            sx={{ color: POLARIS_ROYAL_BLUE }}
                            checked={!inactive}
                            onChange={onCheck}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'QuestionnaireTypeId'}
                            label={'Questionnaire Type'}
                            placeholder="Select questionnaire type..."
                            disabled={
                                !hasRole(acceptedRoles, roles) ||
                                !currentQuestionnaire ||
                                Object.keys(currentQuestionnaire).length < 1
                            }
                            menuItems={questionnaireTypes || []}
                            selectedId={currentQuestionnaire?.typeId || ''}
                            selected={
                                questionnaireTypes?.find(
                                    (el) => el.id === currentQuestionnaire?.typeId
                                ) || {}
                            }
                            onChange={(chosenId) => onQuestionnaireTypeChange(chosenId)}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default QuestionnaireBuilder;
